/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IProductSpecificationViewProps } from '@msdyn365-commerce-modules/product-specification';
import { IProductSpecificationProps, IProductSpecificationData } from '@msdyn365-commerce-modules/product-specification';
import { kRV_DownloadCMSDocumentAsync } from '../../../actions/customProxy/PDPDownloadAbleLinks/DataActionExtension.g';
import { IKRV_DownloadCMSDocumentResponseEntity } from '../../../actions/customProxy/PDPDownloadAbleLinks/DataServiceEntities.g';

enum ProductSpecificationRuleType {
    Blank = 'blank',
    Empty = '',
    AssemblyInstructions = 'Assembly instructions',
    UserManual = 'User Manual',
    WarrantyConditions = 'Warranty Conditions',
    Datasheets = 'Datasheets',
    Zero = 0,
    ProductStatus = 'Product Status',
    DisplayWpPrice = 'Display WP Price',
    IsBundleProduct = 'IsBundleProduct',
    Searchkeywords = 'Search keywords',
    manufacturerItemNumber = 'Manufacturer item number'
}
interface IProductSpecificationButton {
    productName?: string;
    cellData?: string;
    className?: string;
}

const ProductDownloadButton: React.FC<IProductSpecificationButton> = ({ productName, cellData, className }) => (
    <a className={className} href={cellData} target='_blank' type='application/pdf' rel='noreferrer'>
        {productName}
    </a>
);

const ProductSpecificationView: React.FC<IProductSpecificationViewProps &
    IProductSpecificationProps<IProductSpecificationData>> = props => {
    const {
        ProductSpecification,
        ProductSpecificationTableProps,
        ProductSpecificationTableBodyProps,
        productSpecificationResult,
        data: { product }
    } = props;
    const manufacturerItemTypeName = product?.result?.ExtensionProperties?.find(item => {
        if (item.Key === 'ManufItemTypeName') {
            return item;
        } else {
            return null;
        }
    });

    //****** VSI Customization Start for Downloadable Link ******//
    // States use for set and map download able links
    const [productDownloadButtonState, setProductDownloadButtonState] = React.useState<IProductSpecificationButton[] | undefined>([]);
    const [downloadAbleLink, setDownloadAbleLink] = React.useState<IKRV_DownloadCMSDocumentResponseEntity[]>([]);
    // useEffect use to call function and call api to set response
    React.useEffect(() => {
        getDownloadAbleLinks();
    }, [props.data.product]);

    // useEffect use to call function and set downloadable link state
    React.useEffect(() => {
        if (downloadAbleLink) {
            _createDownloadLinks(downloadAbleLink);
        }
    }, [downloadAbleLink]);

    // Function use to call and set state
    const _createDownloadLinks = (content: IKRV_DownloadCMSDocumentResponseEntity[]) => {
        const productDownloadButtonArr: any = [];
        content &&
            content.map((product, index) => {
                productDownloadButtonArr.push(
                    <ProductDownloadButton
                        className='ms-product-specification_button'
                        key={index}
                        productName={product.File}
                        cellData={product.FileURL}
                    />
                );
            });
        setProductDownloadButtonState(productDownloadButtonArr);
    };

    // API CALL
    const getDownloadAbleLinks = async () => {
        let productNumber = props.data.product.result?.ProductNumber!;
        if (props.data.product.result?.Components?.length! > 0) {
            productNumber = productNumber.substring(0, 10)?.trim();
        }
        const oun = props.context.request.apiSettings.oun;
        const context = {
            callerContext: props.context.actionContext
        };
        await kRV_DownloadCMSDocumentAsync(context, productNumber, oun).then(res => {
            setDownloadAbleLink(res);
        });
    };
    //****** VSI Customization End for Downloadable Link ******//

    //VSI-Customizations . To prevent escape characters in attriutes values of product specifications
    // todo need to remove it.
    // interface productSpecificationPropsExtended {
    //     productName?: string;
    // }
    const rowData =
        productSpecificationResult &&
        ArrayExtensions.hasElements(productSpecificationResult) &&
        productSpecificationResult?.filter(ps => {
            const productSpecification = ps;
            // const cellData = productSpecification?.props as JSX.Element;
            const cellData = ps && productSpecification?.props?.children?.props;

            // todo: need to refactor it
            if (ps !== null) {
                if (
                    cellData?.props?.dangerouslySetInnerHTML?.__html ===
                        (ProductSpecificationRuleType.Blank || ProductSpecificationRuleType.Empty) ||
                    cellData?.props?.children === 0
                ) {
                    return null;
                }
                if (
                    cellData?.productName === ProductSpecificationRuleType.AssemblyInstructions.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.Datasheets.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.UserManual.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.WarrantyConditions.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.IsBundleProduct.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.ProductStatus.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.DisplayWpPrice.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.Searchkeywords.trim() ||
                    cellData?.productName === ProductSpecificationRuleType.manufacturerItemNumber.trim()
                ) {
                    return null;
                }
                if (ps?.props.children === ProductSpecificationRuleType.Zero) {
                    return null;
                }
                return !ObjectExtensions.isNullOrUndefined(ps);
            }
            return null;
        });
    const tempArray =
        rowData &&
        rowData?.map((item, index) => {
            const productName = item && item?.props?.children?.props?.productName;
            const value =
                item && item?.props?.children?.props?.cellData?.props?.dangerouslySetInnerHTML?.__html
                    ? item?.props?.children?.props?.cellData?.props?.dangerouslySetInnerHTML?.__html
                    : item?.props?.children?.props?.cellData?.props?.children;

            return {
                key: index, // Use the index as the key attribute
                productName: productName,
                value: value
            };
        });
    /* VSI customization to Move up "manufacturer" */
    const manufacturerIndex = tempArray && tempArray?.findIndex(item => item.productName.includes('Manufacturer'));
    // If found, move the object to the first position in the array
    if (manufacturerIndex !== -1) {
        const manufacturerObject = tempArray && manufacturerIndex && tempArray.splice(manufacturerIndex, 1)[0];
        tempArray && manufacturerObject && tempArray.unshift(manufacturerObject);
    }
    const ManufacturerItemTypeName = {
        key: (tempArray && tempArray.length) || 2,
        productName: 'Manufacturer Item Type Name',
        value: manufacturerItemTypeName?.Value?.StringValue === '' ? 'blank' : manufacturerItemTypeName?.Value?.StringValue
    };
    tempArray && tempArray.splice(1, 0, ManufacturerItemTypeName);
    const finalArray =
        tempArray &&
        tempArray.map((item, index) => ({
            ...item,
            key: index
        }));
    const filteredArray =
        finalArray &&
        finalArray?.filter(
            obj =>
                obj.value !== 'blank' &&
                obj.value !== undefined &&
                obj.value !== 0 &&
                obj.value !== '0' &&
                obj.productName !== 'Promotion Badge' &&
                obj.productName !== 'Promotion Text'
        );
    /* VSI customization Ends */
    return rowData && ArrayExtensions.hasElements(rowData) ? (
        <Module {...ProductSpecification}>
            <div className='product_specification_table'>
                {props.title}

                <Node {...ProductSpecificationTableProps}>
                    <Node {...ProductSpecificationTableBodyProps} tabIndex='1'>
                        {filteredArray &&
                            filteredArray.map(item => (
                                <tr key={item.key} className='ms-product-specification__table-row'>
                                    <th>{item.productName}</th>
                                    <td>{item.value}</td>
                                </tr>
                            ))}
                    </Node>
                </Node>
            </div>
            {productDownloadButtonState?.length! > 0 && (
                <div className='product_specification_download_container'>
                    <h2 className='ms-content-block__title'>Available downloads</h2>
                    <div className='ms-content-block__text'></div>
                    <div className='download-button-container'>{productDownloadButtonState && productDownloadButtonState}</div>
                </div>
            )}
        </Module>
    ) : (
        //VSI-Customizations . To generate downloabale links for documents
        <Module {...ProductSpecification}>
            {productDownloadButtonState?.length! > 0 && (
                <div className='product_specification_download_container'>
                    <h2 className='ms-content-block__title'>Available downloads</h2>
                    <div className='ms-content-block__text'></div>
                    <div className='download-button-container'>{productDownloadButtonState && productDownloadButtonState}</div>
                </div>
            )}
        </Module>
    );
};

export default ProductSpecificationView;
